/**
 * Video player for promotion videos served by promostork_media_server.
 *
 * Handles both plain files (.mp4 / .webm) and HLS playlists (.m3u8):
 *   - Safari / iOS play HLS natively through <video>.
 *   - Chrome / Firefox / Edge need Media Source Extensions glue — hls.js is
 *     lazy-loaded on first use so it stays out of the main bundle.
 *
 * A freshly uploaded video can 404 while the transcode is still running, so
 * a failed load shows a "still processing" state instead of a black box.
 */
import { useEffect, useRef, useState } from 'react';
import { VideoOff } from 'lucide-react';

export interface VideoPlayerProps {
  src: string;
  poster?: string | null;
  className?: string;
  /** Accessible label for the <video> element. */
  label?: string;
}

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const isHlsUrl = (u: string) => /\.m3u8(\?|#|$)/i.test(u);

export function VideoPlayer({ src, poster, className, label }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;
    setFailed(false);

    const nativeHls = video.canPlayType('application/vnd.apple.mpegurl') !== '';
    if (!isHlsUrl(src) || nativeHls) {
      video.src = src;
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    let cancelled = false;
    let hls: import('hls.js').default | null = null;
    (async () => {
      try {
        const { default: Hls } = await import('hls.js');
        if (cancelled) return;
        if (!Hls.isSupported()) {
          video.src = src;
          return;
        }
        hls = new Hls();
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (data.fatal) setFailed(true);
        });
        hls.loadSource(src);
        hls.attachMedia(video);
      } catch (err) {
        console.warn('VideoPlayer: failed to load hls.js, falling back', err);
        if (!cancelled) video.src = src;
      }
    })();

    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [src]);

  return (
    <div className={cn('relative overflow-hidden bg-black', className)}>
      <video
        ref={videoRef}
        poster={poster ?? undefined}
        controls
        playsInline
        preload="metadata"
        aria-label={label}
        onError={() => setFailed(true)}
        className="h-full w-full object-contain"
      />
      {failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-center text-white">
          <VideoOff size={28} aria-hidden="true" />
          <p className="text-sm font-semibold">Video unavailable</p>
          <p className="max-w-xs text-xs text-white/70">
            It may still be processing — please check back in a few minutes.
          </p>
        </div>
      )}
    </div>
  );
}

export default VideoPlayer;
